import * as React from 'react'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import { useAuth } from 'react-oidc-context'
import { connect, useSelector, type ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import SearchBox from '../../../../components/SearchBox'
import LanguageStrings from '../../../../i18n/locales'
import { type RootState } from '../../../../store'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { fetchSiteList } from '../../redux/actionCreators'
import { type SiteListState } from '../../redux/siteListSlice'
import { Button } from '../../../../components/Button'
import EnhancedTable, { type Sort } from './SiteTable'

export interface State extends SnackbarOrigin {
  open: boolean
}

const DeviceListStrings = LanguageStrings().SiteManagementCollectionStrings.SiteListComponent
const DashboardStrings = LanguageStrings().Dashboard

const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}
const searchBoxComponent: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between'
}

function SiteList (props: PropsFromRedux): JSX.Element {
  const [searchValue, setSearchValue] = React.useState('')
  const rowsPerPage = 10
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: '' })
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()

  const navigate = useNavigate()

  /* istanbul ignore next */
  const handleSearch = (): void => {
    setPage(1)
    props.fetchSiteList(page, rowsPerPage, searchValue, sort.order, sort.orderBy)
  }

  function handleBreadCrumbsClick (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    /* istanbul ignore else */
    if (!operationPermissions.updated) {
      props.getOperationPermissions(auth.user?.profile.sub as string)
    }
  }, [])

  return (
    <div>
      <div role='presentation' data-testid='breadcrumbs' onClick={handleBreadCrumbsClick} className='AmiHeading' >
        <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
          onClick={() => { navigate(-1) }}
        />
        <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
          <Link aria-label='navBreadcrumb' className='assetmgmtli' underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
            {DashboardStrings.HOME}
          </Link>
          <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{DashboardStrings.SiteManagement}</Typography>
        </Breadcrumbs>
      </div>
      <div className='ArrowDeviceList'>
        <div>
          <span data-testid='deviceHistoryDetails' className='SiteListBorderComponent' style={{ fontWeight: '700' }}>{DeviceListStrings.Site}</span>
          <span className='TotalSiteComponent'> {DeviceListStrings.Total} <b>{props.siteList.siteCount}</b> {DeviceListStrings.Site}</span>
        </div>
        {
          operationPermissions.permissions.can_add_tiq_sites
            ? <Button sx={{ fontFamily: 'Honeywell Sans Web', marginRight: '1em', display: 'flex', alignItems: 'center', padding: '6px 20px' }} width='fit-content' onClick={() => { navigate('/create-site') }} dataTestId='create-site-btn'>{DeviceListStrings.AddNewSite}</Button>
            : <></>
        }
      </div>
      <div className='configurations-table-heading'>
        <h5>{DeviceListStrings.SiteList}</h5>
      </div>
      <div style={style}>
        <>
          <div style={searchBoxComponent}>
            <SearchBox
              sx={{ width: '384px' }}
              placeholder={DeviceListStrings.Search}
              immediate={true}
              emptyEnterFallback={() => { props.fetchSiteList(page, rowsPerPage, searchValue, sort.order, sort.orderBy) }}
              searchVal={searchValue}
              setSearchVal={setSearchValue}
              performAction={handleSearch}
            />
          </div>
          <EnhancedTable
            searchVal={searchValue}
            page={page}
            rowsPerPage={rowsPerPage}
            sort={sort}
            setPage={setPage}
            setSort={setSort}
            performAction={() => { props.fetchSiteList(page, rowsPerPage, searchValue, sort.order, sort.orderBy) }}
          />
        </>
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchSiteList: (
    PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string
  ) => void
  getOperationPermissions: (sub: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchSiteList: (
    PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string
  ) => dispatch(fetchSiteList(PageNumber, PageItems, searchKey, SortParams, sortField)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
})

interface StateToProps {
  siteList: SiteListState
  grandParentBreadcrumb: string
}

const mapStateToProps = (state: RootState): StateToProps => ({
  siteList: state.siteManagementSiteList,
  grandParentBreadcrumb: state.grandParentBreadcrumb
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(SiteList)
